import { Icon } from '@iconify/react';
import React, { useState, useEffect } from 'react';
import config from '../config';
import auth from '../helper/auth';

const EmployeeManagementLayer = () => {
    const [employees, setEmployees] = useState([]);
    const [positions, setPositions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentId, setCurrentId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    // Form State
    const [formData, setFormData] = useState({
        full_name: '',
        email: '',
        password: '',
        position_id: ''
    });

    useEffect(() => {
        fetchEmployees();
        fetchPositions();
    }, []);

    const fetchEmployees = async () => {
        setLoading(true);
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/employees`);
            if (!response.ok) throw new Error('Failed to fetch employees');
            const data = await response.json();
            setEmployees(data);
        } catch (error) {
            console.error('Error fetching employees:', error);
            // Optional: Show error toast
        } finally {
            setLoading(false);
        }
    };

    const fetchPositions = async () => {
        try {
            // Using auth.fetch even if public, good practice
            const response = await auth.fetch(`${config.API_BASE_URL}/api/positions`);
            if (!response.ok) throw new Error('Failed to fetch positions');
            const data = await response.json();
            setPositions(data);
        } catch (error) {
            console.error('Error fetching positions:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const openAddModal = () => {
        setFormData({ full_name: '', email: '', password: '', position_id: '' });
        setIsEditing(false);
        setCurrentId(null);
        setShowModal(true);
    };

    const openEditModal = (emp) => {
        setFormData({
            full_name: emp.full_name,
            email: emp.email,
            password: '', // Don't show current password
            position_id: emp.position_id || ''
        });
        setIsEditing(true);
        setCurrentId(emp.id);
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.full_name || !formData.email || !formData.position_id) {
            alert('Name, Email and Position are required');
            return;
        }

        if (!isEditing && !formData.password) {
            alert('Password is required for new employees');
            return;
        }

        try {
            const url = isEditing 
                ? `${config.API_BASE_URL}/api/employees/${currentId}`
                : `${config.API_BASE_URL}/api/employees`;
            
            const method = isEditing ? 'PUT' : 'POST';

            const response = await auth.fetch(url, {
                method: method,
                body: JSON.stringify(formData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Operation failed');
            }

            setShowModal(false);
            fetchEmployees();
        } catch (error) {
            console.error('Error saving employee:', error);
            alert(error.message);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this employee? This action cannot be undone.')) return;

        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/employees/${id}`, {
                method: 'DELETE'
            });

            if (!response.ok) throw new Error('Failed to delete employee');

            fetchEmployees();
        } catch (error) {
            console.error('Error deleting employee:', error);
            alert('Failed to delete employee');
        }
    };

    // Filter employees
    const filteredEmployees = employees.filter(emp => 
        emp.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (emp.position_title && emp.position_title.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <div className="card h-100 p-0 radius-12">
            {/* Header */}
            <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                <div className="d-flex align-items-center gap-3">
                    <h6 className="text-lg fw-semibold mb-0">Employee Management</h6>
                </div>
                <div className="d-flex align-items-center flex-wrap gap-3">
                    <div className="search-box">
                        <input 
                            type="text" 
                            className="form-control" 
                            placeholder="Search employees..." 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <button 
                        onClick={openAddModal}
                        className="btn btn-primary d-flex align-items-center gap-2"
                    >
                        <Icon icon="ic:round-plus" className="text-xl" />
                        Add New Employee
                    </button>
                </div>
            </div>

            {/* Body */}
            <div className="card-body p-24">
                {loading ? (
                    <div className="text-center p-5">Loading...</div>
                ) : (
                    <div className="table-responsive scroll-sm">
                        <table className="table bordered-table sm-table mb-0">
                            <thead>
                                <tr>
                                    <th scope="col">Name</th>
                                    <th scope="col">Email</th>
                                    <th scope="col">Position</th>
                                    <th scope="col">Status</th>
                                    <th scope="col">Created At</th>
                                    <th scope="col" className="text-center">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredEmployees.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="text-center p-4">No employees found.</td>
                                    </tr>
                                ) : filteredEmployees.map(emp => (
                                    <tr key={emp.id}>
                                        <td className="fw-medium">
                                            <div className="d-flex align-items-center gap-2">
                                                <div className="w-32-px h-32-px bg-primary-light rounded-circle d-flex align-items-center justify-content-center text-white fw-bold">
                                                    {emp.full_name.charAt(0).toUpperCase()}
                                                </div>
                                                {emp.full_name}
                                            </div>
                                        </td>
                                        <td>{emp.email}</td>
                                        <td>
                                            {emp.position_title ? (
                                                <span className="badge bg-neutral-100 text-neutral-600 px-2 py-1 rounded-pill">
                                                    {emp.position_title}
                                                </span>
                                            ) : (
                                                <span className="text-muted text-sm">-</span>
                                            )}
                                        </td>
                                        <td>
                                            <span className={`badge px-2 py-1 rounded-pill ${emp.status === 'active' ? 'bg-success-focus text-success-main' : 'bg-danger-focus text-danger-main'}`}>
                                                {emp.status}
                                            </span>
                                        </td>
                                        <td>{new Date(emp.created_at).toLocaleDateString()}</td>
                                        <td className="text-center">
                                            <div className="d-flex align-items-center justify-content-center gap-2">
                                                <button 
                                                    onClick={() => openEditModal(emp)}
                                                    className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                                >
                                                    <Icon icon="lucide:edit" />
                                                </button>
                                                <button 
                                                    onClick={() => handleDelete(emp.id)}
                                                    className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                                >
                                                    <Icon icon="mingcute:delete-2-line" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal */}
            {showModal && (
                <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
                        <div className="modal-content radius-16">
                            <div className="modal-header">
                                <h5 className="modal-title">{isEditing ? 'Edit Employee' : 'Add New Employee'}</h5>
                                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <form onSubmit={handleSubmit}>
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold">Full Name <span className="text-danger">*</span></label>
                                            <input 
                                                type="text" 
                                                className="form-control" 
                                                name="full_name" 
                                                value={formData.full_name} 
                                                onChange={handleInputChange} 
                                                placeholder="John Doe"
                                                required
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold">Email <span className="text-danger">*</span></label>
                                            <input 
                                                type="email" 
                                                className="form-control" 
                                                name="email" 
                                                value={formData.email} 
                                                onChange={handleInputChange} 
                                                placeholder="john@example.com"
                                                required
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold">Position <span className="text-danger">*</span></label>
                                            <select 
                                                className="form-select" 
                                                name="position_id" 
                                                value={formData.position_id} 
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="">Select Position</option>
                                                {positions.map(pos => (
                                                    <option key={pos.id} value={pos.id}>{pos.title}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold">
                                                {isEditing ? 'New Password (Optional)' : 'Password'} 
                                                {!isEditing && <span className="text-danger">*</span>}
                                            </label>
                                            <input 
                                                type="password" 
                                                className="form-control" 
                                                name="password" 
                                                value={formData.password} 
                                                onChange={handleInputChange} 
                                                placeholder={isEditing ? "Leave blank to keep current" : "Enter password"}
                                                required={!isEditing}
                                                minLength="6"
                                            />
                                            {isEditing && <div className="form-text text-xs">Only enter if you want to change the password.</div>}
                                        </div>
                                    </div>

                                    <div className="d-flex justify-content-end gap-2 mt-4">
                                        <button type="button" className="btn btn-secondary-light" onClick={() => setShowModal(false)}>Cancel</button>
                                        <button type="submit" className="btn btn-primary">{isEditing ? 'Update Employee' : 'Create Employee'}</button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmployeeManagementLayer;
